import React, { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { LocalNotifications } from '@capacitor/local-notifications';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Loading from '../components/Loading';
import { useHeader } from '@/contexts/HeaderContext';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FINAL_PAYWALL_URL = '/onboarding?step=paywall&paywall_step=24';

function Settings({ userId }) {
  const { t, i18n } = useTranslation();
  // debug: verify that the imported subcomponents are defined in test env
  console.log('Settings render start: Loading', Loading);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';
  const { user: contextUser, isAdmin, isPremium, loading: userLoading } = useUser();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState(true); // promemoria giornaliero
  const [eventRemindersEnabled, setEventRemindersEnabled] = useState(true); // promemoria eventi
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Push notifications are managed by the device; UI toggle removed.

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/user/${userId}`);
        setProfile(response.data);
        // Sync notification toggles from backend
        if (response.data.daily_reminder_enabled !== undefined) {
          setNotifications(response.data.daily_reminder_enabled);
        }
        if (response.data.event_reminders_enabled !== undefined) {
          setEventRemindersEnabled(response.data.event_reminders_enabled);
        }
        // Sync language with i18n — respect localStorage preference over server
        const savedLang = localStorage.getItem('betterhusbandUserLang');
        const userLanguage = savedLang || response.data.language || i18n.language || 'en';
        if (i18n.language !== userLanguage) {
          i18n.changeLanguage(userLanguage);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, i18n]);
  // useHeader: set global header instead of rendering an internal header
  const { setHeaderProps } = useHeader();
  useEffect(() => {
    setHeaderProps({ title: t('settings.title') });
  }, [t, setHeaderProps]);

  // Gestione promemoria giornaliero - scheduling handled by CapacitorInit.js
  // This effect only handles cancellation when user disables notifications
  useEffect(() => {
    if (!notifications) {
      // Cancella le notifiche giornaliere (IDs usati da CapacitorInit e vecchi ID)
      LocalNotifications.cancel({ notifications: [{ id: 101 }, { id: 1001 }] }).catch(console.error);
    }
  }, [notifications]);

  // Gestione promemoria check-in serale - scheduling handled by CapacitorInit.js
  // This effect only handles cancellation when user disables notifications
  useEffect(() => {
    if (!notifications) {
      LocalNotifications.cancel({ notifications: [{ id: 102 }, { id: 1002 }] }).catch(console.error);
    }
  }, [notifications]);

  // Gestione promemoria eventi (placeholder: solo toggle visivo)
  useEffect(() => {
    // Qui puoi aggiungere la logica di scheduling/cancellazione notifiche eventi se necessario
    // Per ora solo feedback visivo
  }, [eventRemindersEnabled]);

  // ADMIN testing helper
  const handleAdminProgTest = () => {
    console.log('Admin progressive questions test triggered');
    toast.success(t('settings.adminProgQuestionsClicked', 'Admin test triggered'));
    // navigate to dashboard and ask it to show the progressive modal immediately
    navigate('/dashboard?showProgressive=1');
  };

  // helper to update a simple field on the user profile
  const updateSetting = async (field, value) => {
    try {
      await axios.put(`${API}/user/${userId}`, { [field]: value });
      setProfile({ ...profile, [field]: value });
      if (field === 'language') {
        i18n.changeLanguage(value);
      }
      toast.success(t('settings.saved'));
    } catch (error) {
      toast.error(t('settings.error'));
    }
  };
  // Push handling removed from UI — device will manage push subscriptions.

  // Delete account handler
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/account/delete-me`);
      // Sign out from Supabase
      try { await supabase.auth.signOut(); } catch (_) {}
      // Clear all local storage
      localStorage.clear();
      toast.success(t('settings.deleteAccount.success'));
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(t('settings.deleteAccount.error'));
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Toggle Switch Component
  const Toggle = ({ enabled, onChange, disabled = false }) => (
    typeof enabled === 'boolean' && typeof onChange === 'function' ? (
      <button
        onClick={() => !disabled && onChange(!enabled)}
        disabled={disabled}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
          disabled 
            ? 'bg-stone-200 dark:bg-[#2E2E36] cursor-not-allowed' 
            : enabled 
              ? 'bg-[#5C8748]' 
              : 'bg-stone-300 dark:bg-[#3A3A3A]'
        }`}
      >
        <div
          className={`absolute top-0.5 w-5 h-5 bg-white dark:bg-[#0E0E10] rounded-full shadow-sm transition-transform duration-200 ${
            enabled ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    ) : null
  );

  // Setting Row Component
  const SettingRow = ({ title, description, children, onClick, showArrow }) => {
    const isClickable = Boolean(onClick);
    const Comp = isClickable ? 'button' : 'div';
    if (!title) return null;
    return (
      <Comp
        {...(isClickable ? { onClick, type: 'button' } : {})}
        className={`w-full flex items-center justify-between py-3 ${isClickable ? 'hover:bg-stone-50 dark:hover:bg-[#222228] -mx-4 px-4 rounded-full cursor-pointer' : ''}`}
      >
        <div className="text-left flex-1 min-w-0">
          <p className="text-sm font-medium text-stone-900 dark:text-[#EFEFEF]">{title}</p>
          {description && (
            <p className="text-xs text-stone-500 dark:text-[#606060] mt-0.5">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          {children}
          {showArrow && <ChevronRight className="w-4 h-4 text-stone-400" />}
        </div>
      </Comp>
    );
  };

  // Section Component
  const Section = ({ title, children }) => (
    title ? (
      <div className="mb-6">
        <h2 className="text-xs font-semibold text-stone-400 dark:text-[#606060] uppercase tracking-wider mb-2 px-1">
          {title}
        </h2>
        <div className="bg-inherit rounded-xl">
          <div className="px-4">
            {children}
          </div>
        </div>
      </div>
    ) : null
  );

  const handleSubscriptionClick = () => {
    const subscriptionStatus = contextUser?.subscription_status || profile?.subscription_status;
    const hasActiveSubscription = isPremium || subscriptionStatus === 'premium' || subscriptionStatus === 'trial';

    if (!userLoading && contextUser && !contextUser.needs_onboarding && !hasActiveSubscription) {
      navigate(FINAL_PAYWALL_URL);
      return;
    }

    navigate('/subscription');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0E0E10] flex items-center justify-center page-container">
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  // debug output when rendering
  console.log('Settings render - isAdmin', isAdmin, 'userLoading', userLoading, 'user', contextUser);
  return (
    <div style={{ background: 'inherit', display: 'flex', justifyContent: 'center', overflowX: 'hidden' }}>
<div style={{ width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', minHeight: 844, background: 'inherit', position: 'relative', paddingBottom: 80, overflowX: 'hidden', overflowY: 'auto' }}>

      <div style={{ padding: '24px 24px 0' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, stroke: 'var(--c-texPri)', fill: 'none', strokeWidth: 1.5 }}><polyline points="15 18 9 12 15 6"/></svg>
            <span style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 18, color: 'var(--c-texPri)' }}>{i18n.language?.startsWith('it') ? 'Indietro' : i18n.language?.startsWith('es') ? 'Atrás' : 'Back'}</span>
          </button>
        </div>

        {/* admin-only floating button for progressive questions testing */}
        {isAdmin && (
          <button
            onClick={handleAdminProgTest}
            aria-label={t('settings.adminProgQuestions', 'Progressive questions')}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 56,
              height: 56,
              borderRadius: 28,
              background: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              color: '#fff',
              fontSize: 24,
              cursor: 'pointer'
            }}
          >
            ?
          </button>
        )}

        <div style={{ height: 1, background: 'var(--c-border)', margin: '12px 24px' }} />

        <div style={{ padding: '0 24px' }}>
          {/* Language Section */}
          <div style={{ paddingTop: 28 }}> 
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--c-texSec)', marginBottom: 10, fontWeight: 600 }}>{t('settings.language')}</div>
            <div style={{ background: 'var(--c-bg-secondary, #F5F0EB)', borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' }} onClick={() => updateSetting('language', 'it')}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>Italiano</div>
                </div>
                <div>
                  {profile?.language === 'it' && (
                    <div style={{ width: 20, height: 20, borderRadius: 9999, background: 'var(--c-texPri)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg style={{ width: 10, height: 10, color: 'var(--c-surface)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L16 7" /></svg>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.04)', cursor: 'pointer' }} onClick={() => updateSetting('language', 'en')}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>English</div>
                </div>
                <div>
                  {profile?.language === 'en' && (
                    <div style={{ width: 20, height: 20, borderRadius: 9999, background: 'var(--c-texPri)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg style={{ width: 10, height: 10, color: 'var(--c-surface)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L16 7" /></svg>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.04)', cursor: 'pointer' }} onClick={() => updateSetting('language', 'es')}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>Español</div>
                </div>
                <div>
                  {profile?.language === 'es' && (
                    <div style={{ width: 20, height: 20, borderRadius: 9999, background: 'var(--c-texPri)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg style={{ width: 10, height: 10, color: 'var(--c-surface)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L16 7" /></svg>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Notifications Section */}
          <div style={{ paddingTop: 28 }}>
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--c-texSec)', marginBottom: 10, fontWeight: 600 }}>{t('settings.notifications')}</div>

            <div style={{ background: 'var(--c-bg-secondary, #F5F0EB)', borderRadius: 16, overflow: 'hidden' }}>
              {/* Dark Mode Toggle — admin only */}
              {isAdmin && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
                  <div>
                    <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{i18n.language?.startsWith('it') ? 'Tema scuro' : i18n.language?.startsWith('es') ? 'Tema oscuro' : 'Dark mode'}</div>
                    <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)', marginTop: 4 }}>{i18n.language?.startsWith('it') ? 'Attiva il tema scuro' : i18n.language?.startsWith('es') ? 'Activar el tema oscuro' : 'Enable dark theme'}</div>
                  </div>
                  <Toggle enabled={isDark} onChange={(val) => setTheme(val ? 'dark' : 'light')} />
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', ...(isAdmin ? { borderTop: '1px solid rgba(0,0,0,0.04)' } : {}) }}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.dailyReminder')}</div>
                  <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)', marginTop: 4 }}>{t('settings.dailyReminderDesc')}</div>
                </div>
                <Toggle enabled={notifications} onChange={(val) => { setNotifications(val); updateSetting('daily_reminder_enabled', val); }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.eventReminders')}</div>
                  <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)', marginTop: 4 }}>{t('settings.eventRemindersDesc')}</div>
                </div>
                <Toggle enabled={eventRemindersEnabled} onChange={(val) => { setEventRemindersEnabled(val); updateSetting('event_reminders_enabled', val); }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.reminderTime')}</div>
                  <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)', marginTop: 4 }}>{profile?.reminder_time || '19:00'}</div>
                </div>
                <input
                  type="time"
                  value={profile?.reminder_time || '19:00'}
                  onChange={(e) => updateSetting('reminder_time', e.target.value)}
                  style={{ width: 80, textAlign: 'right', border: 'none', background: 'transparent', color: 'var(--c-text-input)' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.checkinTime')}</div>
                  <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)', marginTop: 4 }}>{profile?.checkin_time || '22:00'}</div>
                </div>
                <input
                  type="time"
                  value={profile?.checkin_time || '22:00'}
                  onChange={(e) => updateSetting('checkin_time', e.target.value)}
                  style={{ width: 80, textAlign: 'right', border: 'none', background: 'transparent', color: 'var(--c-text-input)' }}
                />
              </div>
            </div>
          </div>

          {/* Account Section */}
          <div style={{ paddingTop: 28 }}>
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--c-texSec)', marginBottom: 10, fontWeight: 600 }}>{t('settings.account')}</div>
            <div style={{ background: 'var(--c-bg-secondary, #F5F0EB)', borderRadius: 16, overflow: 'hidden' }}>
              <div onClick={handleSubscriptionClick} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.subscription')}</div>
                  <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)', marginTop: 4 }}>{t('settings.freePlan')}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400" />
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div style={{ paddingTop: 28 }}>
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#c0392b', marginBottom: 10, fontWeight: 600 }}>{t('settings.dangerZone')}</div>
            <div style={{ background: '#fef2f2', borderRadius: 16, overflow: 'hidden' }}>
              {!showDeleteConfirm ? (
                <div
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' }}
                >
                  <div>
                    <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: '#c0392b' }}>{t('settings.deleteAccount.title')}</div>
                    <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)', marginTop: 4 }}>{t('settings.deleteAccount.description')}</div>
                  </div>
                  <ChevronRight className="w-4 h-4" style={{ color: '#c0392b' }} />
                </div>
              ) : (
                <div style={{ padding: '20px' }}>
                  <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 14, color: '#c0392b', marginBottom: 12, textAlign: 'center' }}>
                    {t('settings.deleteAccount.confirmMessage')}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 8,
                        border: '1px solid var(--c-border)', background: 'var(--c-surface)',
                        fontFamily: 'Manrope-Variable, serif', fontSize: 14,
                        color: 'var(--c-texPri)', cursor: 'pointer'
                      }}
                    >
                      {t('settings.deleteAccount.cancel')}
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 8,
                        border: 'none', background: '#8B0000',
                        fontFamily: 'Manrope-Variable, serif', fontSize: 14,
                        color: '#fff', cursor: deleting ? 'wait' : 'pointer',
                        opacity: deleting ? 0.6 : 1
                      }}
                    >
                      {deleting ? t('settings.deleteAccount.deleting') : t('settings.deleteAccount.confirm')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* About Section */}
          <div style={{ paddingTop: 28, paddingBottom: 40 }}>
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--c-texSec)', marginBottom: 10, fontWeight: 600 }}>{t('settings.about')}</div>
            <div style={{ background: 'var(--c-bg-secondary, #F5F0EB)', borderRadius: 16, overflow: 'hidden' }}>
              <div onClick={() => navigate('/privacy')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' }}>
                <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.privacy')}</div>
                <ChevronRight className="w-4 h-4 text-stone-400" />
              </div>
              <div onClick={() => navigate('/terms')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.04)', cursor: 'pointer' }}>
                <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.terms')}</div>
                <ChevronRight className="w-4 h-4 text-stone-400" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                <div style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 16, color: 'var(--c-texPri)' }}>{t('settings.version')}</div>
                <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, color: 'var(--c-texSec)' }}>0.1.31</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
